// Cave84 — Custom Wine Regions hook
// Reads owner-defined wine regions from Firestore `settings/wine_regions`.
// These are merged with the 14 hardcoded regions in VintageChartsPage.
// Document shape: { custom: Array<{ id: string; label: string; sub: string }> }

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  doc, getDoc, setDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuthStore } from '@/stores/auth.store';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomWineRegion {
  id: string;     // e.g. 'Priorat', 'Barossa_Valley' — must be unique, URL-safe
  label: string;  // Display name, e.g. 'Priorat'
  sub: string;    // Sub-label, e.g. 'Catalonia'
}

interface WineRegionsDoc {
  custom: CustomWineRegion[];
  updated_at?: unknown;
}

const DOC_PATH = 'settings/wine_regions';
const QK = ['settings', 'wine_regions'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function fetchCustomRegions(): Promise<CustomWineRegion[]> {
  const snap = await getDoc(doc(db, DOC_PATH));
  if (!snap.exists()) return [];
  const data = snap.data() as WineRegionsDoc;
  return data.custom ?? [];
}

async function saveCustomRegions(regions: CustomWineRegion[]): Promise<void> {
  await setDoc(doc(db, DOC_PATH), {
    custom: regions,
    updated_at: serverTimestamp(),
  });
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useCustomWineRegions() {
  return useQuery({
    queryKey: QK,
    queryFn: fetchCustomRegions,
    staleTime: 5 * 60_000,
  });
}

export function useAddCustomWineRegion() {
  const qc = useQueryClient();
  const role = useAuthStore.getState().userDoc?.role;

  return useMutation({
    mutationFn: async (region: CustomWineRegion) => {
      if (role !== 'owner') throw new Error('Owner role required.');
      const current = await fetchCustomRegions();
      if (current.find((r) => r.id === region.id)) {
        throw new Error(`Region "${region.id}" already exists.`);
      }
      await saveCustomRegions([...current, region]);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QK }); },
  });
}

export function useDeleteCustomWineRegion() {
  const qc = useQueryClient();
  const role = useAuthStore.getState().userDoc?.role;

  return useMutation({
    mutationFn: async (regionId: string) => {
      if (role !== 'owner') throw new Error('Owner role required.');
      const current = await fetchCustomRegions();
      await saveCustomRegions(current.filter((r) => r.id !== regionId));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QK }); },
  });
}

/** Convenience: returns a stable slugify function for building region IDs */
export function useSlugify() {
  return useCallback((label: string): string => {
    return label
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  }, []);
}
